import React, { useState, useEffect, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, TextField, Button, Typography, IconButton,
  Autocomplete, CircularProgress, Checkbox, FormControlLabel,
  Divider, InputAdornment, Chip, Alert,
} from "@mui/material";
import {
  Close as CloseIcon, Edit as EditIcon,
  CheckCircle as CheckCircleIcon, Send as SendIcon,
} from "@mui/icons-material";
import api from "../../../services/api";

const BLUE = {
  solight: "#EEF7FE", light: "#1976D2", main: "#2196F3",
  dark: "#1565C0", white: "#FFFFFF",
  gradient: "linear-gradient(90deg, #1976D2 0%, #42A5F5 100%)",
  gradientDark: "linear-gradient(90deg, #0D47A1 0%, #1976D2 100%)",
};

const EMAIL_REGEX  = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;

const validateEmail  = (v) => {
  if (!v?.trim())                return "Email is required";
  if (!EMAIL_REGEX.test(v.trim())) return "Enter a valid email address";
  return "";
};
const validateMobile = (v) => {
  if (!v?.trim())                 return "Calling number is required";
  if (!MOBILE_REGEX.test(v.trim())) return "Enter a valid 10-digit mobile number";
  return "";
};

// ── OTP Input ────────────────────────────────────────────────────────────────
const OtpInput = ({ value, onChange, disabled }) => {
  const refs = useRef([]);
  const digits = (value || "").split("").slice(0, 6);
  while (digits.length < 6) digits.push("");

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[idx] = val;
    onChange(next.join(""));
    if (val && idx < 5) refs.current[idx + 1]?.focus();
  };

  const handleKey = (e, idx) => {
    if (e.key === "Backspace") {
      const next = [...digits];
      if (next[idx]) { next[idx] = ""; onChange(next.join("")); }
      else if (idx > 0) refs.current[idx - 1]?.focus();
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mt: 1 }}>
      {digits.map((d, i) => (
        <TextField
          key={i}
          inputRef={(el) => (refs.current[i] = el)}
          value={d}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKey(e, i)}
          disabled={disabled}
          inputProps={{ maxLength: 1, style: { textAlign: "center", fontSize: "1.2rem", fontWeight: 700 } }}
          sx={{ width: 44 }}
          size="small"
        />
      ))}
    </Box>
  );
};

// ── VerifyField ──────────────────────────────────────────────────────────────
const VerifyField = ({
  label, fieldKey, value, originalValue, verified,
  otpSent, otpValue, otpError, countdown, loading,
  isDarkMode, onChange, onOtpChange, onSendOtp, onVerify,
  type = "text", fieldError,
  blocked = false,
  blockedMessage = "",
}) => {
  const isChanged  = value !== originalValue;
  const showVerify = isChanged && !verified;

  return (
    <Box>
      <TextField
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        fullWidth size="small" type={type}
        error={Boolean(fieldError)}
        helperText={fieldError || ""}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {verified && !isChanged && (
                <CheckCircleIcon sx={{ color: "success.main", fontSize: 18 }} />
              )}
              {showVerify && (
                <Button
                  size="small" variant="outlined"
                  onClick={onSendOtp}
                  disabled={loading || countdown > 0}
                  sx={{ fontSize: "0.7rem", py: 0.3, px: 1, minWidth: "auto",
                    borderColor: BLUE.main, color: BLUE.main, textTransform: "none" }}
                  startIcon={loading
                    ? <CircularProgress size={10} />
                    : <SendIcon sx={{ fontSize: "12px !important" }} />}
                >
                  {countdown > 0 ? `${countdown}s` : otpSent ? "Resend" : "Verify"}
                </Button>
              )}
            </InputAdornment>
          ),
        }}
        sx={{ mb: otpSent && showVerify ? 1 : 0 }}
      />

        {/* Block mobile OTP until email is verified */}
        {blocked && isChanged && (
            <Alert severity="info" sx={{ mt: 0.5, py: 0.3, fontSize: "0.75rem" }}>
            {blockedMessage}
            </Alert>
        )}

      {otpSent && showVerify && !blocked &&(
        <Box sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${BLUE.main}22`, backgroundColor: BLUE.solight }}>
          <Typography variant="caption" color="text.secondary">
            Enter the 6-digit OTP sent to your {fieldKey === "mobile" ? "mobile" : "email"}
          </Typography>
          <OtpInput value={otpValue} onChange={onOtpChange} disabled={loading} />
          {otpError && (
            <Alert severity="error" sx={{ mt: 1, py: 0.3, fontSize: "0.75rem" }}>{otpError}</Alert>
          )}
          <Button
            fullWidth variant="contained"
            onClick={onVerify}
            disabled={loading || otpValue.length < 6}
            sx={{ mt: 1, background: BLUE.gradient, textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            {loading ? <CircularProgress size={16} sx={{ color: "white" }} /> : "Confirm OTP"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ── Main Dialog ───────────────────────────────────────────────────────────────
const EditLeaderDspocDialog = ({
  open, onClose, member, tabType, isDarkMode = false, onSaveSuccess,
}) => {
  const [form, setForm] = useState({
    fullname: "",
    designation: null,        // { id, title }
    local_designation: "",
    domain_email: "",
    calling_number: "",
    whatsapp_number: "",
    sameAsCalling: false,
  });

  const [original, setOriginal]         = useState({ domain_email: "", calling_number: "" });
  const [designations, setDesignations] = useState([]);
  const [desigLoading, setDesigLoading] = useState(false);

  // OTP — mobile
  const [mobileOtpSent, setMobileOtpSent]       = useState(false);
  const [mobileOtp, setMobileOtp]               = useState("");
  const [mobileVerified, setMobileVerified]     = useState(false);
  const [mobileOtpError, setMobileOtpError]     = useState("");
  const [mobileCountdown, setMobileCountdown]   = useState(0);
  const [mobileOtpLoading, setMobileOtpLoading] = useState(false);

  // OTP — email
  const [emailOtpSent, setEmailOtpSent]       = useState(false);
  const [emailOtp, setEmailOtp]               = useState("");
  const [emailVerified, setEmailVerified]     = useState(false);
  const [emailOtpError, setEmailOtpError]     = useState("");
  const [emailCountdown, setEmailCountdown]   = useState(0);
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);

  const [saveLoading, setSaveLoading]   = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError]               = useState("");
  const [errors, setErrors]             = useState({
    fullname: "", designation: "", domain_email: "", calling_number: "", whatsapp_number: "",
  });

  const startCountdown = (setter, seconds = 60) => {
    setter(seconds);
    const iv = setInterval(() => {
      setter((p) => { if (p <= 1) { clearInterval(iv); return 0; } return p - 1; });
    }, 1000);
  };

  useEffect(() => {
    if (!open || !member) return;
    fetchDesignations();
    fetchMemberDetails();
    resetOtpStates();
    // eslint-disable-next-line
  }, [open, member]);

  const resetOtpStates = () => {
    setMobileOtpSent(false); setMobileOtp(""); setMobileVerified(false);
    setMobileOtpError(""); setMobileCountdown(0);
    setEmailOtpSent(false); setEmailOtp(""); setEmailVerified(false);
    setEmailOtpError(""); setEmailCountdown(0);
    setError("");
    setErrors({ fullname: "", designation: "", domain_email: "", calling_number: "", whatsapp_number: "" });
  };

  // ── Fetch designations → uses id, title ──────────────────────────────────
  const fetchDesignations = async () => {
    setDesigLoading(true);
    try {
      const res = await api.get("/staff/designations");
      // expects: [{ id, title }, ...]
      setDesignations(res.data || []);
    } catch { setDesignations([]); }
    finally { setDesigLoading(false); }
  };

  const parseError = (e, fallback = "Something went wrong.") => {
    const detail = e?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((d) => d.msg).join(", ");
    return fallback;
    };

  // ── Fetch member from academy_leaders ────────────────────────────────────
  const fetchMemberDetails = async () => {
    setFetchLoading(true);
    try {
      // Both leader and dspoc live in academy_leaders, same endpoint pattern
      const id = member.leader_id || member.id;
      const endpoint = tabType === "leader"
        ? `/staff/leaders/${id}`
        : `/staff/dspoc/${id}`;

      const res = await api.get(endpoint);
      const d   = res.data;

      // Map exact academy_leaders column names
      const initial = {
        fullname:          d.fullname          || "",
        // ✅ designation is a plain string — wrap into { id, title } using title as key
        designation:       d.designation
                            ? { id: d.designation, title: d.designation }
                            : null,
        local_designation: d.local_designation || "",
        domain_email:      d.domain_email      || "",
        calling_number:    d.calling_number    || "",
        whatsapp_number:   d.whatsapp_number   || "",
        sameAsCalling:     d.calling_number === d.whatsapp_number,
        };

      setForm(initial);
      setOriginal({
        domain_email:   d.domain_email   || "",
        calling_number: d.calling_number || "",
      });
    } catch {
      setError("Failed to load member details.");
    } finally {
      setFetchLoading(false);
    }
  };

  const setField = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleCallingChange = (val) => {
    setField("calling_number", val);
    if (form.sameAsCalling) setField("whatsapp_number", val);
  };

  const handleSameAsCalling = (checked) => {
    setForm((p) => ({
      ...p,
      sameAsCalling:   checked,
      whatsapp_number: checked ? p.calling_number : p.whatsapp_number,
    }));
  };

  const handleBlur = (field, value) => {
    let msg = "";
    if (field === "fullname"       && !value.trim())          msg = "Full name is required";
    if (field === "domain_email")                             msg = validateEmail(value);
    if (field === "calling_number")                           msg = validateMobile(value);
    if (field === "whatsapp_number" && !form.sameAsCalling)   msg = validateMobile(value);
    setErrors((p) => ({ ...p, [field]: msg }));
  };

  const emailNeedsVerify =
  form.domain_email !== original.domain_email && !emailVerified;

  // OTP handlers (unchanged logic, same endpoints)
  const sendMobileOtp = async () => {
    // Block if email change is pending verification
    if (form.domain_email !== original.domain_email && !emailVerified) {
        setError("Please verify your new email address before verifying mobile.");
        return;
    }
    setMobileOtpLoading(true); setMobileOtpError("");
    try {
      await api.post("/staff/send-otp-mobile", { user_uuid: member.uuid, mobile: form.calling_number });
      setMobileOtpSent(true); setMobileOtp("");
      startCountdown(setMobileCountdown);
    } catch (e) {
        const status = e?.response?.status;
        const detail = parseError(e, "Failed to send OTP.");

        if (status === 409) {
            setErrors((p) => ({ ...p, calling_number: detail })); // "Mobile number already exists."
        } else {
            setMobileOtpError(detail);
        }
        }
    finally { setMobileOtpLoading(false); }
  };

  const verifyMobileOtp = async () => {
  setMobileOtpLoading(true);
  setMobileOtpError("");
  try {
    await api.post("/staff/verify-otp-mobile", {
      user_uuid: member.uuid,
      otp: mobileOtp,
    });
    setMobileVerified(true);
    setMobileOtpSent(false);
  } catch (e) {
    setMobileOtpError(parseError(e, "Invalid OTP. Try again."));
  } finally {
    setMobileOtpLoading(false);
  }
};

  const sendEmailOtp = async () => {
    const emailErr = validateEmail(form.domain_email);
    if (emailErr) {
        setErrors((p) => ({ ...p, domain_email: emailErr }));
        return;
    }

    setEmailOtpLoading(true);
    setEmailOtpError("");
    setErrors((p) => ({ ...p, domain_email: "" }));

    try {
        await api.post("/staff/send_otp_dspoc", {
        user_uuid: member.uuid,
        email: form.domain_email,
        });
        setEmailOtpSent(true);
        setEmailOtp("");
        startCountdown(setEmailCountdown);
    } catch (e) {
        const status = e?.response?.status;
        const detail = parseError(e, "Failed to send OTP.");

        if (status === 409) {
        // Show inline field error, not generic error
        setErrors((p) => ({ ...p, domain_email: detail })); // "Email already exists"
        } else {
        setEmailOtpError(detail);
        }
    } finally {
        setEmailOtpLoading(false);
    }
    };

  const verifyEmailOtp = async () => {
    setEmailOtpLoading(true); setEmailOtpError("");
    try {
      await api.post("/staff/verify-otp", { user_uuid: member.uuid, otp: emailOtp });
      setEmailVerified(true); setEmailOtpSent(false);
    } catch (e) { setEmailOtpError(parseError(e, "Invalid OTP. Try again.")); }
    finally { setEmailOtpLoading(false); }
  };

  // ── Save — validates all mandatory fields ────────────────────────────────
  const handleSave = async () => {
    const nameErr   = !form.fullname.trim()   ? "Full name is required"    : "";
    // ✅ designation is now mandatory
    const desigErr  = !form.designation       ? "Designation is required"  : "";
    const emailErr  = validateEmail(form.domain_email);
    const mobileErr = validateMobile(form.calling_number);
    const waErr     = !form.sameAsCalling ? validateMobile(form.whatsapp_number) : "";

    setErrors({
      fullname: nameErr, designation: desigErr,
      domain_email: emailErr, calling_number: mobileErr, whatsapp_number: waErr,
    });

    if (nameErr || desigErr || emailErr || mobileErr || waErr) return;

    if (form.calling_number !== original.calling_number && !mobileVerified) {
      setError("Please verify your new mobile number before saving."); return;
    }
    if (form.domain_email !== original.domain_email && !emailVerified) {
      setError("Please verify your new email address before saving."); return;
    }

    setSaveLoading(true); setError("");
    try {
      const id = member.leader_id || member.id;
      const endpoint = tabType === "leader"
        ? `/staff/leaders/${id}`
        : `/staff/dspoc/${id}`;

      // ✅ Exact academy_leaders column names in payload
      await api.put(endpoint, {
        fullname:          form.fullname,
        // designation_id:    form.designation.id,      // FK to designations.id
        designation: form.designation.title,
        local_designation: form.local_designation,
        domain_email:      form.domain_email,
        calling_number:    form.calling_number,
        whatsapp_number:   form.whatsapp_number,
      });

      onSaveSuccess?.();
      onClose();
    } catch (e) {
      setError(parseError(e, "Failed to save. Please try again."));
    } finally { setSaveLoading(false); }
  };

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: isDarkMode ? "#1A1A2E" : "#FFFFFF",
          color: isDarkMode ? "white" : "inherit",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: isDarkMode ? BLUE.gradientDark : BLUE.gradient,
          color: BLUE.white,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          py: 1.5, px: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EditIcon sx={{ fontSize: 20 }} />
          <Typography fontWeight={700} fontSize="1rem">
            Edit {tabType === "leader" ? "Leader" : "DSPOC"} Details
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: BLUE.white }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5, px: 3 }}>
        {fetchLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 3 }}>
            {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

            {/* Full Name — mandatory */}
            <TextField
              label="Full Name *"
              value={form.fullname}
              onChange={(e) => setField("fullname", e.target.value)}
              onBlur={(e) => handleBlur("fullname", e.target.value)}
              error={Boolean(errors.fullname)}
              helperText={errors.fullname}
              fullWidth size="small"
              InputLabelProps={{ shrink: true }}
            />

            {/* Designation — mandatory, uses id + title */}
            <Autocomplete
              options={designations}
              getOptionLabel={(opt) => opt.title || ""}        // ✅ title not name
              value={form.designation}
              onChange={(_, val) => {
                setField("designation", val);
                setErrors((p) => ({ ...p, designation: val ? "" : "Designation is required" }));
              }}
              loading={desigLoading}
              size="small"
              isOptionEqualToValue={(opt, val) => opt.title === val?.title}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Designation *"
                  error={Boolean(errors.designation)}
                  helperText={errors.designation}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {desigLoading && <CircularProgress size={14} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Local Designation — optional */}
            <TextField
              label="Local Designation"
              value={form.local_designation}
              onChange={(e) => setField("local_designation", e.target.value)}
              fullWidth size="small"
              InputLabelProps={{ shrink: true }}
            />

            <Divider sx={{ my: 0.5 }} />

            {/* Domain Email — mandatory + OTP */}
            <VerifyField
              label="Domain Email *"
              fieldKey="email"
              value={form.domain_email}
              fieldError={errors.domain_email}
              originalValue={original.domain_email}
              verified={emailVerified}
              otpSent={emailOtpSent}
              otpValue={emailOtp}
              otpError={emailOtpError}
              countdown={emailCountdown}
              loading={emailOtpLoading}
              isDarkMode={isDarkMode}
              onChange={(val) => {
                setField("domain_email", val);
                setEmailVerified(false); setEmailOtpSent(false);
                handleBlur("domain_email", val);
              }}
              onOtpChange={setEmailOtp}
              onSendOtp={sendEmailOtp}
              onVerify={verifyEmailOtp}
            />

            {/* Calling Number — mandatory + OTP */}
            <VerifyField
              label="Calling Number *"
              fieldKey="mobile"
              value={form.calling_number}
              fieldError={errors.calling_number}
              originalValue={original.calling_number}
              verified={mobileVerified}
              otpSent={mobileOtpSent}
              otpValue={mobileOtp}
              otpError={mobileOtpError}
              countdown={mobileCountdown}
              loading={mobileOtpLoading}
              isDarkMode={isDarkMode}
              onChange={(val) => {
                handleCallingChange(val);
                setMobileVerified(false); setMobileOtpSent(false);
                handleBlur("calling_number", val);
              }}
              onOtpChange={setMobileOtp}
              onSendOtp={sendMobileOtp}
              onVerify={verifyMobileOtp}
              type="tel"
              blocked={emailNeedsVerify}
              blockedMessage="Please verify your new email first"
            />

            {/* Same as Calling */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.sameAsCalling}
                  onChange={(e) => handleSameAsCalling(e.target.checked)}
                  size="small"
                  sx={{ color: BLUE.main, "&.Mui-checked": { color: BLUE.main } }}
                />
              }
              label={<Typography variant="body2" fontSize="0.8rem">WhatsApp number same as calling number</Typography>}
            />

            {/* WhatsApp Number */}
            {!form.sameAsCalling ? (
              <TextField
                label="WhatsApp Number"
                value={form.whatsapp_number}
                onChange={(e) => setField("whatsapp_number", e.target.value)}
                onBlur={(e) => handleBlur("whatsapp_number", e.target.value)}
                error={Boolean(errors.whatsapp_number)}
                helperText={errors.whatsapp_number}
                fullWidth size="small" type="tel"
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  label="WhatsApp Number"
                  value={form.calling_number}
                  fullWidth size="small" disabled
                  InputLabelProps={{ shrink: true }}
                />
                <Chip
                  label="Auto-synced" size="small"
                  sx={{ bgcolor: BLUE.solight, color: BLUE.dark, fontWeight: 600, flexShrink: 0 }}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onClose} variant="outlined"
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave} variant="contained"
          disabled={saveLoading || fetchLoading}
          sx={{
            borderRadius: 2, textTransform: "none", fontWeight: 600,
            background: BLUE.gradient, minWidth: 100,
          }}
        >
          {saveLoading
            ? <CircularProgress size={16} sx={{ color: "white" }} />
            : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLeaderDspocDialog;